import { ANY_BARBER } from '../../constants';
import events from './events';
import Integrations from './integrations';
import Analytics from './Analytics';

/**
 * Class that implements methods that track customer behavior through process of booking
 * Following events are tracked:
 *  'page_loaded - fires when page is loaded
 *  'query_entered - fires when visitor perform search for shop
 *  'select_shop - fires when a customer clicks on a shop
 *  'choose_professional_clicked' or 'choose_service_clicked' depending on what option a customer clicks
 *  'select_barber' - fires when a customer clicks on a barber
 *  'select_any_barber' - fires when a customer clicks on “book with any profession” button
 *  'select_service' - fires when a customer clicks on a particular service
 *  'select_date' - fires when a customer clicks on a day
 *  'select_time' - fires when a customer clicks on a time slot
 *  'review_and_book' - fires when a customer clicks on "Review & Book" button
 *  'review_and_pay' - fires when a customer clicks on "Review & Pay" button (Apple Pay)
 *  'waiting_list' - fires when a customer clicks on "Put me on a waiting list" button
 *  'waiting_list_with_apple_pay' - fires when a customer clicks on "Put me on a waiting list" button (Apple Pay)
 *  'fill_personal_info' - fires when a customer fills his info and book buttons become available
 *  'reserve_and_pay' - fires when a customer clicks on 'Reserve & Pay'/'Process' button
 *  'reserve_and_provide_card' - fires when a customer clicks on 'Reserve' button
 *  'reserve' - fires when a customer clicks on book without card
 *  'show_stripe_modal' - fires when stripe payment modal is opened
 *  'show_squire_modal' - fires when squire payment modal is opened
 *  'start_payment' - fires when a customer opens payment modal (custom or stripe default)
 *  'cc_number_entered' - fires when a customer adds 'well-formed and potentially complete' cc number
 *  'cc_expiration_date_entered' - fires when a customer adds 'well-formed and potentially complete' cc expiration date
 *  'cc_security_code_entered' - fires when a customer adds 'well-formed and potentially complete' cc cvc code
 *  'cc_zip_code_entered' - fires when a customer adds 'well-formed and potentially complete' cc zip code
 *  'tip_option_active' - fires if tips are enabled on checkout page/modal
 *  'tip_selected' - fires when tip is selected
 *  'fill_card_info' - fires when a customer fills card info (only with custom modal)
 *  'abandon_payment' - fires when a customer closes payment modal
 *  'payment_error' - fires when we receive payment error from the backend
 *  'booking_complete' - fires when a customer successfully books an appointment
 *  'only_pay_active' - (only reserve and pay is shown)
 *  'reserve_and_provide_card_active' - (reserve and pay and reserve and provide card are both shown)
 *  'reserve_without_pay_active' - (reserve and pay and reserve without providing a card are both shown)
 *  'only_reserve_active' - (only a reserve without payment is shown... I think this is only possible with kiosk mode)
 *  'promo_active' - fires when third party promotion is shown to customer on checkout page
 *
 *  @see {@link https://stripe.com/docs/js/element/events/on_change?type=cardElement#element_on_change-handler-complete|StripeJS}
 *       For 'well-formed and potentially complete' field value
 */
class BookingAnalytics extends Analytics {
  pageLoaded(options = null) {
    return this.fireEvent(events.PAGE_LOAD, options);
  }

  queryShop(options = null) {
    return this.fireEvent(events.QUERY_SHOP, options);
  }

  selectShop(shop) {
    return this.fireEvent(events.SELECT_SHOP, { shop: shop.name, brandId: shop.brandId });
  }

  chooseProfessional() {
    return this.fireEvent(events.CHOOSE_PROFESSIONAL_CLICKED);
  }

  chooseService() {
    return this.fireEvent(events.CHOOSE_SERVICE_CLICKED);
  }

  selectBarber(barber, { shop = {} }) {
    const isAnyBarber = barber === ANY_BARBER;
    const event = isAnyBarber ? events.SELECT_ANY_BARBER : events.SELECT_BARBER;

    return this.fireEvent(event, isAnyBarber ? undefined : {
      barber: `${barber.firstName} ${barber.lastName}`,
      shop: shop.name,
    });
  }

  selectService(service, { shop = {} }) {
    return this.fireEvent(events.SELECT_SERVICE, { service, shop: shop.name });
  }

  selectDate(date) {
    return this.fireEvent(events.SELECT_DATE, { date });
  }

  selectTime(time, { shop = {} }) {
    return this.fireEvent(events.SELECT_TIME, { time, shop: shop.name });
  }

  proceedToCheckout({
    service,
    barber,
    date,
    time,
    isApplePay,
    applePayAvailable,
    waitingList,
    shop,
  }) {
    if (service) {
      this.selectService(service, { shop });
    }
    if (barber) {
      this.selectBarber(barber, { shop });
    }
    this.selectDate(date);
    this.selectTime(time, { shop });

    if (waitingList) {
      return isApplePay ? this.waitingListWithApplePay() : this.waitingList({
        applePayAvailable: !!applePayAvailable,
      });
    }

    return isApplePay ? this.reviewAndPay() : this.reviewAndBook({
      applePayAvailable: !!applePayAvailable,
    });
  }

  reviewAndBook(otherOptions) {
    return this.fireEvent(events.REVIEW_AND_BOOK, { otherOptions });
  }

  reviewAndPay() {
    return this.fireEvent(events.REVIEW_AND_PAY);
  }

  waitingList(otherOptions) {
    return this.fireEvent(events.WAITING_LIST, { otherOptions });
  }

  waitingListWithApplePay() {
    return this.fireEvent(events.WAITING_LIST_WITH_APPLE_PAY);
  }

  fillPersonalInfo() {
    return this.fireEvent(events.FILL_PERSONAL_INFO);
  }

  startPayment(options) {
    return this.fireEvent(events.START_PAYMENT, options);
  }

  fillCardInfoField(inputType, options = null) {
    const eventMap = {
      cardNumber: events.CREDIT_CARD_NUMBER_ENTERED,
      cardExpiry: events.CREDIT_CARD_EXP_DATE_ENTERED,
      cardCvc: events.CREDIT_CARD_CVC_ENTERED,
      cardZipCode: events.CREDIT_CARD_ZIP_CODE_ENTERED,
    };

    if (!eventMap[inputType]) {
      return null;
    }

    return this.fireEvent(eventMap[inputType], options);
  }

  creditCardNumberEntered(options = null) {
    return this.fillCardInfoField('cardNumber', options);
  }

  creditCardExpDateEntered(options = null) {
    return this.fillCardInfoField('cardExpiry', options);
  }

  creditCardCvcEntered(options = null) {
    return this.fillCardInfoField('cardCvc', options);
  }

  creditCardZipCodeEntered(options = null) {
    return this.fillCardInfoField('cardZipCode', options);
  }

  tipOptionActive(options = null) {
    return this.fireEvent(events.TIP_OPTION_ACTIVE, options);
  }

  tipSelected(options = null) {
    return this.fireEvent(events.TIP_SELECTED, options);
  }

  fillCardInfo({ source, isApplePay = false }) {
    return this.fireEvent(events.FILL_CARD_INFO, { source, isApplePay });
  }

  abandonPayment(options) {
    return this.fireEvent(events.ABANDON_PAYMENT, options);
  }

  paymentError(options) {
    return this.fireEvent(events.PAYMENT_ERROR, options);
  }

  reserveAndPay(options) {
    return this.fireEvent(events.RESERVE_AND_PAY, options);
  }

  reserveAndProvideCard(options) {
    return this.fireEvent(events.RESERVE_AND_PROVIDE_CARD, options);
  }

  reserve(options) {
    return this.fireEvent(events.RESERVE, options);
  }

  bookingComplete(options) {
    if (window.stylzGtag) {
      window.stylzGtag('event', 'conversion', {
        send_to: 'AW-10926152046/sXAsCL-G6sIDEO6y_9ko',
        value: (options.totalWithTip / 100).toFixed(2),
        currency: 'USD',
        transaction_id: options.appointmentId,
      });
    }

    return this.fireEvent(events.BOOKING_COMPLETE, options);
  }

  showStripeModal(options) {
    return this.fireEvent(events.SHOW_STRIPE_MODAL, options);
  }

  showSquireModal(options) {
    return this.fireEvent(events.SHOW_SQUIRE_MODAL, options);
  }

  buttonsActive({ reserve, bookAndPay, reserveWithCard, shop }) {
    if (bookAndPay && !reserve && !reserveWithCard) {
      return this.fireEvent(events.ONLY_PAY_ACTIVE, { shop: shop.name });
    }
    if (bookAndPay && reserveWithCard) {
      return this.fireEvent(events.RESERVE_AND_PROVIDE_CARD_ACTIVE, { shop: shop.name });
    }
    if (bookAndPay && reserve) {
      return this.fireEvent(events.RESERVE_WITHOUT_PAY_ACTIVE, { shop: shop.name });
    }
    if (!bookAndPay && reserve && !reserveWithCard) {
      return this.fireEvent(events.ONLY_RESERVE_ACTIVE, { shop: shop.name });
    }

    return false;
  }

  thirdPartyPromoActive(context) {
    return this.fireEvent(events.PROMO_ACTIVE, { context });
  }

  fireEvent(event, options = null) {
    // eslint-disable-next-line no-underscore-dangle
    return super.fireEvent(this._getEventName(event), options);
  }

  // eslint-disable-next-line class-methods-use-this
  _getEventName(event) {
    return event;
  }
}

const analytics = new BookingAnalytics(Integrations);
export default analytics;



// WEBPACK FOOTER //
// ./src/app/applicationServices/analytics/BookingAnalytics.js